import type { ReactElement } from "react"
import { useContext, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid, useMediaQuery, useTheme } from "@mui/material"
import { type InputChangeEvent, InputText, Pagination, Select } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"

import { BaseBarChart } from "../../../components/graphs/Bar/BaseBarChart"
import { KonanAPI } from "../../../services/KonanAPI"
import { InsightList } from "../../../types/generated/api/InsightList"
import { ReportContext } from "../Context"
import { setLabelsCriteria } from "../Slice"
import { generateLabelCriteriaInsightData } from "../helpers"
import type { InsightProps, LabelsCriteriaInsightProps } from "../interfaces"

/**
 * LabelReasonInsight Component
 *
 * Renders a breakdown visualization of reasons for label assignments in the decision-making process.
 * Fetches insight data from the API and displays it using the ReasonsBreakdownChart component.
 *
 * @param {InsightProps} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item containing a ReasonsBreakdownChart
 */
export const LabelCriteriaInsight = (props: InsightProps): React.ReactElement => {
  const { insight } = props
  const { id: projectId } = useParams<{ id: string }>()
  const dispatch = useDispatch()

  const { report } = useContext(ReportContext)

  const theme = useTheme()
  const lg = useMediaQuery(theme.breakpoints.up("lg"))

  // Fetch label-reasons insight data from the API
  const { data: labelReasonInsight, isLoading: isLoadingLabelReasonInsights } = useQuery<
    AxiosResponse<LabelsCriteriaInsightProps>,
    AxiosError
  >(
    ["label-criteria", projectId, report?.uuid, insight.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      onSuccess: ({ data }) => {
        dispatch(setLabelsCriteria(data))
      },
      // Only enable the query when all required parameters are available
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty = !labelReasonInsight?.data.data || _isEmpty(labelReasonInsight?.data.data)

  // Process the label data and calculate percentages
  const adjustedData = useMemo(
    () =>
      generateLabelCriteriaInsightData({
        data: labelReasonInsight?.data,
        isLoading: isLoadingLabelReasonInsights,
        isEmpty: isEmpty,
      }),
    [isEmpty, isLoadingLabelReasonInsights, labelReasonInsight?.data],
  )

  const sliceCount = useMemo((): number => {
    const labelReasonsCount = Object.keys(adjustedData.labels).length

    if (lg && labelReasonsCount) {
      return Math.min(2, labelReasonsCount)
    }

    return 1
  }, [adjustedData.labels, lg])

  return (
    <Grid container item xs={12}>
      {new Array(sliceCount).fill(0).map((_, index) => (
        <Grid
          item
          xs={sliceCount === 1 ? 12 : 6}
          pr={index === 0 && sliceCount !== 1 ? 2 : 0}
          key={`label-criteria-${index}-${isLoadingLabelReasonInsights}`}
        >
          <LabelReasonGraph
            data={adjustedData}
            isLoading={isLoadingLabelReasonInsights}
            isEmpty={isEmpty}
            status={insight.status}
            index={index}
          />
        </Grid>
      ))}
    </Grid>
  )
}

type LabelReasonGraphProps = {
  data: {
    labels: string[]
    labelReasons: Array<Record<string, string | number>>
    reasons: Record<string, string[]>
  }
  isEmpty: boolean
  isLoading: boolean
  status?: InsightList.status
  index: number
}

export const LabelReasonGraph = (props: LabelReasonGraphProps): ReactElement => {
  const { data, isEmpty, isLoading, status, index } = props

  const { t } = useTranslation("insights")

  const [selectedLabel, setSelectedLabel] = useState<string | null | undefined>(data.labels[index])
  const [selectedReasons, setSelectedReasons] = useState<Array<string> | undefined>(undefined)

  const [page, setPage] = useState<number>(0) // State for current page number
  const [pageSize, setPageSize] = useState<number>(3) // State for number of items per page

  // Handler to update selected reasons based on the selected label
  const handleLabelSelect = (e: InputChangeEvent<Element>): void => {
    // Set the selected label to the value from the event
    setSelectedLabel(e.target.value as string)

    // Update the selected reasons based on the newly selected label
    setSelectedReasons((prev) => {
      // Filter the previous selected reasons to only include those that match the new label
      const filteredReasons = prev?.filter((reason) => data.reasons[e.target.value]?.includes(reason)) ?? []
      // Return the filtered reasons or undefined if no reasons are left
      return filteredReasons?.length > 0 ? filteredReasons : undefined
    })
  }

  // Handler to update selected reasons
  const handReasonSelect = (selectedOptions: string[]): void => {
    if (selectedOptions.length === 0) {
      setSelectedReasons(undefined) // Reset selected reasons if none are selected
      return
    }
    setSelectedReasons(selectedOptions) // Update selected reasons
  }

  /**
   * Subheader component that displays label and reason selection controls.
   * @returns {React.ReactElement} The subheader element containing select inputs for labels and reasons.
   */
  const subHeader = (): React.ReactElement => {
    return (
      <Grid container columnSpacing={1}>
        <Grid item xs={6}>
          <Select
            label={t("LabelCriteriaInsight.label.label")}
            options={data.labels}
            value={selectedLabel ?? ""}
            handleChange={handleLabelSelect}
            isLoadingOptions={isLoading}
            fullWidth
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        {selectedLabel && (
          <Grid item xs={6}>
            <InputText
              label={t("LabelCriteriaInsight.label.reason")}
              options={data.reasons[selectedLabel]}
              value={
                selectedReasons === undefined
                  ? t("LabelCriteriaInsight.reasons.all")
                  : selectedReasons?.length && selectedReasons?.length > 0
                    ? t("LabelCriteriaInsight.reasons.selected", { count: selectedReasons?.length })
                    : t("LabelCriteriaInsight.reasons.select")
              }
              multipleModeProps={{
                isActive: true,
                hideChips: true,
                selectedValues: (selectedReasons as string[]) ?? [],
                setSelectedValues: (selectedOptions) => handReasonSelect(selectedOptions as string[]),
                allowDeselect: (selectedReasons?.length ?? 0) > 0,
              }}
              isLoadingOptions={isLoading}
              isSelect
              fullWidth
              hideDescription
              menuProps={{ menuMaxContent: true }}
            />
          </Grid>
        )}
      </Grid>
    )
  }

  const adjustedData =
    data.labelReasons.length > 0
      ? data.labelReasons.filter(
          (item) =>
            // if item label matches the selected label
            item.reason === selectedLabel &&
            // and if the reasons is included in the selected reasons array OR the selectedReasons array is empty
            (!selectedReasons ||
              selectedReasons?.length === 0 ||
              selectedReasons?.includes((item.label as string) ?? "")),
        )
      : []

  const feedbacks = useMemo((): string[] => {
    if (adjustedData.length > 0)
      return Object.keys(data.labelReasons[0] ?? "").filter(
        (feedbackLabel) => !["label", "reason"].includes(feedbackLabel),
      )

    return []
  }, [adjustedData.length, data.labelReasons])

  return (
    <BaseBarChart
      title={t("LabelCriteriaInsight.title")}
      data={adjustedData.slice(page * pageSize, (page + 1) * pageSize) ?? []}
      isLoading={isLoading}
      subHeader={subHeader()}
      footer={
        <Grid item display={"flex"} justifyContent={"flex-end"}>
          <Pagination
            variant={"complex"}
            totalCount={adjustedData.length}
            page={page + 1}
            onPageChange={(page: number) => setPage(page - 1)}
            pageSize={pageSize}
            onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
            hidePageSize
          />
        </Grid>
      }
      graphHeight={300}
      indexBy={"label"}
      keys={feedbacks}
      groupMode="stacked"
      emptyState={
        status !== InsightList.status.SUCCEEDED
          ? {
              title: `${t("label.insight")} ${t(`status.${status?.toLocaleLowerCase()}`, {
                defaultValue: status,
              })?.toLocaleLowerCase()}`,
            }
          : isEmpty
            ? { title: t("LabelCriteriaInsight.unavailable") }
            : undefined
      }
    />
  )
}
